import { useAppDispatch } from "@/store/hooks/hooks";
import { openVideoModal, VideoModalTab } from "@/store/slices/videoModalSlice";
import { AnimatePresence, easeOut, motion } from "framer-motion";
import { ChevronDown, Workflow } from "lucide-react";
import { useState } from "react";

const ToolsDropdown: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);

  type Tool = {
    title: string;
    tab: VideoModalTab;
    color: string;
    hover: string;
  };

  const tools: Tool[] = [
    {
      title: "Mockup",
      tab: "mockup",
      color: "bg-blue-600",
      hover: "hover:bg-blue-700",
    },
    {
      title: "Catalog Builder",
      tab: "catalog-builder",
      color: "bg-purple-600",
      hover: "hover:bg-purple-700",
    },
  ];

  const toggleOpen = () => setIsOpen((prev) => !prev);
  const closeOpen = () => setIsOpen(false);

  return (
    <div className="relative my-1 bg-blue-50 rounded-lg">
      <motion.button
        onClick={toggleOpen}
        initial={false}
        animate={{ backgroundColor: isOpen ? "#EFF6FF" : "#F0F9FF" }}
        transition={{ duration: 0.2 }}
        className="w-full text-left p-2 transition-colors rounded-lg border border-blue-100"
      >
        <h3 className="font-semibold text-gray-800 text-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-white rounded-md shadow-sm text-blue-600">
              <Workflow className="w-5 h-5" />
            </div>
            <span>How Our Tools Works?</span>
          </div>
          <motion.span
            className="ml-2 inline-block"
            initial={false}
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <ChevronDown className="w-5 h-5 text-blue-400" />
          </motion.span>
        </h3>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="desktop-expansion"
            className="
              hidden md:block
              px-2 space-y-2 
              relative z-10 overflow-hidden
            "
            initial={{ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 }}
            animate={{ opacity: 1, height: "auto", paddingTop: "0.5rem", paddingBottom: "0.5rem" }}
            exit={{ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
            }}
          >
            {tools.map((tool) => (
              <motion.button
                key={tool.tab}
                onClick={() => {
                  dispatch(openVideoModal(tool.tab));
                  closeOpen();
                }}
                className={`
                  block px-4 py-2 text-sm text-gray-700 w-full text-left bg-blue-100 
                  hover:text-blue-600  hover:bg-blue-200 transition-colors rounded-md
                  font-semibold
                `}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.1 }}
              >
                {tool.title}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="mobile-dropdown"
            className="
              md:hidden
              absolute left-0 right-0 top-full mt-1 
              bg-white border border-gray-200 rounded-lg shadow-xl z-30
              px-4 overflow-hidden
            "
            initial={{ opacity: 0, y: -8, height: 0, paddingTop: 0, paddingBottom: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto", paddingTop: "0.75rem", paddingBottom: "0.75rem" }}
            exit={{ opacity: 0, y: -8, height: 0, paddingTop: 0, paddingBottom: 0 }}
            transition={{
              duration: 0.2,
              ease: easeOut,
            }}
          >
            <div className="flex flex-col gap-3">
              {tools.map((tool) => (
                <motion.button
                  key={tool.tab}
                  onClick={() => {
                    dispatch(openVideoModal(tool.tab));
                    closeOpen();
                  }}
                  className={`w-full px-4 py-2 ${tool.color} ${tool.hover} text-white  text-left rounded-md text-sm font-semibold transition-colors`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.1 }}
                >
                  {tool.title}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ToolsDropdown;
